import { NgStyle } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { strToColor } from '@reflact/tsutil';
import { HintComponent } from '@shared-angular/components/hint/hint.component';
import JSZip from 'jszip';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TagModule } from "primeng/tag";
import { FormForBooleanComponent } from 'src/app/shared/components/ai-bot/utils/form-for-boolean/form-for-boolean.component';
import * as XLSX from 'xlsx';
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { RouteShareService } from '../../../../../../shared/services/route-share.service';
import { SupervisorChatComponent } from "./supervisor-chat/supervisor-chat.component";
import { SupervisorFunctionExecComponent } from './supervisor-function-exec/supervisor-function-exec.component';
import { SupervisorPromptInputComponent } from "./supervisor-prompt-input/supervisor-prompt-input.component";
import { SupervisorThreadBroadcastService } from './supervisor-threads/supervisor-thread-broadcast.service';
import { SupervisorThreadsComponent } from "./supervisor-threads/supervisor-threads.component";
import { SupervisorService } from './supervisor.service';


@Component({
  imports: [RouterLink, NgStyle,
    FormsModule,
    SupervisorFunctionExecComponent,
    SupervisorChatComponent,
    SupervisorThreadsComponent,
    SupervisorPromptInputComponent,
    HintComponent,
    FormForBooleanComponent,
    TooltipModule,
    ManualLinkComponent,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputGroupModule,
    InputNumberModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    CheckboxModule,
    TagModule,
    MessageModule
  ],
  templateUrl: './supervisor-tab.component.html',
  styleUrl: './supervisor-tab.component.scss'
})
export class SupervisorTabComponent implements AfterViewInit {
  @ViewChild("wrapper") public wrapper?: ElementRef<HTMLDivElement>;
  public headOffset?: number = 320;
  public supervisorActive = $localize`:@@supervisor-active:Supervisor aktiv`;
  protected chatSearchInput: string = '';
  public filterOnlineOnly = false;
  public filterWithNotifications = false;
  public filterMinMessages = 0;

  public get hasActiveFilters(): boolean {
    return !!(this.chatSearchInput || this.filterOnlineOnly || this.filterWithNotifications || this.filterMinMessages > 0);
  }

  public resetFilters(): void {
    this.chatSearchInput = '';
    this.filterOnlineOnly = false;
    this.filterWithNotifications = false;
    this.filterMinMessages = 0;
    this.supervisorService.filterThreads('');
  }

  public getThreadColor(userId: string): string {
    return strToColor(userId.substring(6));
  }

  constructor(
    public supervisorService: SupervisorService,
    public rss: RouteShareService,
    public threadBroadcastService: SupervisorThreadBroadcastService,
    private router: Router
  ) { }

  public ngAfterViewInit(): void {
    setTimeout(() => { this.calculateHeaderHeight(); });
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.calculateHeaderHeight();
  }

  private calculateHeaderHeight(): void {
    if (this.wrapper) {
      this.headOffset = this.wrapper.nativeElement.offsetTop;
    }
  }

  public isSelectedChatActive(): boolean {
    const selectedThread = this.supervisorService.selectedThread;
    const id = selectedThread?.[0]?.threadId;
    if (id) {
      return this.supervisorService.currentActiveThreads.includes(id);
    }
    return false;
  }

  public selectAllThreads() {
    this.threadBroadcastService.toggleAllThreadsForBroadcast();
    this.supervisorService.selectedThread = undefined;
    void this.router.navigate(['/bot-admin', this.rss.botConfig._id, 'supervisor']);
  }

  public async downloadSelectedChatsAsZip(): Promise<void> {
    const zip = new JSZip();

    const labelConnect = $localize`:@@connected:Verbunden`;
    const labelDisconnect = $localize`:@@socketEvent-disconnected:Verbindung getrennt`;
    const labelSystem = $localize`:@@excel-role-system:system`;

    for (const threadId of this.threadBroadcastService.selectedThreadsSet) {
      const messages = this.supervisorService.historyItems
        .filter(item => item.threadId === threadId);
      const socketEvents = this.supervisorService.socketEvents
        .filter(e => e.openAiThreadId === threadId);

      type TimelineItem =
        | { kind: 'message'; created: Date; role: string; text: string }
        | { kind: 'event'; created: Date; label: string };

      const timeline: TimelineItem[] = [
        ...messages.map(msg => ({
          kind: 'message' as const,
          created: new Date(msg.created),
          role: msg.role,
          text: msg.content.map(c => (c.type === 'text' ? c.text : '[Bild]')).join(' ')
        })),
        ...socketEvents.map(e => ({
          kind: 'event' as const,
          created: new Date(e.created),
          label: e.type === 'connect' ? labelConnect : labelDisconnect
        }))
      ].sort((a, b) => a.created.getTime() - b.created.getTime());

      const rows: string[][] = [[
        $localize`:@@excel-col-timestamp:Zeitstempel`,
        $localize`:@@excel-col-role:Rolle`,
        $localize`:@@excel-col-message:Nachricht`
      ]];
      for (const item of timeline) {
        const ts = item.created.toLocaleString('de-DE');
        if (item.kind === 'message') {
          rows.push([ts, item.role, item.text]);
        } else {
          rows.push([ts, labelSystem, item.label]);
        }
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 80 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Chat');
      const xlsxBytes = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const userId = messages[0]?.context?.user ?? '';
      const userName = this.supervisorService.getUserIdToName(userId) || threadId.slice(0, 8);
      const safeName = userName.replace(/[^a-z0-9_-]/gi, '_');
      zip.file(`${safeName}_${threadId.slice(0, 8)}.xlsx`, xlsxBytes);
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supervisor-chats-${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
